// OrdenForm.jsx
import React, { useState, useEffect, useRef } from "react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  buildDisplayNameFromUser,
  buildDisplayNameFromMaybeObject,
  resolveDisplayName,
} from "../../lib/displayName";
import SignatureCanvas from "react-signature-canvas";
import OrdenFormMobile from "./OrdenFormMobile";
import { useIsMobile } from "../../hooks/useDeviceType";
import { X, Save, User, Building, Calendar, Clock, Wrench, FileText, CheckSquare, PenTool, Maximize2, Trash2, Layers } from 'lucide-react';
import SearchableSelect from "../ui/SearchableSelect";

export default function OrdenForm({ orden, onClose, onSaved, agendamientoInicial }) {
  const isMobile = useIsMobile();
  
  // Si es móvil, usar la versión móvil (sin firma digital por simplicidad)
  if (isMobile) {
    return <OrdenFormMobile orden={orden} onClose={onClose} onSaved={onSaved} />;
  }
  const { user } = useAuth();
  const sigCanvas = useRef(null);
  const fullSigCanvas = useRef(null);
  const [fullScreenDims, setFullScreenDims] = useState({ width: 0, height: 0 });

  // Resolución estándar para guardar la firma (igual que canvas principal)
  const STANDARD_SIG_WIDTH = 600;
  const STANDARD_SIG_HEIGHT = 180;

  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [todosLosEquipos, setTodosLosEquipos] = useState([]); // Todos los equipos del cliente (sin filtrar)
  const [sucursales, setSucursales] = useState([]);
  const [departamentosDisponibles, setDepartamentosDisponibles] = useState([]);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [cargaInicialCompleta, setCargaInicialCompleta] = useState(false);
  const inicializadoRef = useRef(false);
  const [modoInicializacion, setModoInicializacion] = useState(!!orden);
  const [agendamientos, setAgendamientos] = useState([]);
  const [cargandoAgendamientos, setCargandoAgendamientos] = useState(false);
  const [ticketsCliente, setTicketsCliente] = useState([]);
  const [cargandoTickets, setCargandoTickets] = useState(false);
  const currentUserDisplayName = buildDisplayNameFromUser(user);

  const deriveNombreTecnicoDesdeOrden = (ordenData) =>
    resolveDisplayName({
      primary: ordenData?.nombre_tecnico,
      secondary: [
        ordenData?.tecnico_nombre,
        ordenData?.tecnico,
        ordenData?.tecnico_detalle,
        ordenData?.usuario_creador_nombre,
        ordenData?.usuario_creador,
      ],
      lookup: () => {
        if (!ordenData?.tecnico_id) return "";
        if (
          user &&
          (ordenData.tecnico_id === user?._id || ordenData.tecnico_id === user?.id)
        ) {
          return currentUserDisplayName;
        }
        return "";
      },
    });

  // Helpers de fecha / timezone
  const getLocalDateString = (date) => {
    let d;
    if (date) {
      d = new Date(date);
    } else {
      // Si no se provee fecha, usar la actual de Paraguay explícitamente
      try {
        d = new Date(new Date().toLocaleString("en-US", {timeZone: "America/Asuncion"}));
      } catch (e) {
        console.warn("Error setting Paraguay timezone, falling back to local", e);
        d = new Date();
      }
    }

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`; // formato YYYY-MM-DD para input[type=date]
  };

  const parseISOToLocalDate = (iso) => {
    if (!iso) return getLocalDateString();
    // Asegurarnos de que la fecha se interprete en la zona horaria correcta
    const d = new Date(iso);
    const offset = d.getTimezoneOffset();
    const correctedDate = new Date(d.getTime() + offset * 60000);
    return getLocalDateString(correctedDate);
  };

  const parseISOToLocalTime = (iso) => {
    if (!iso) return "";
    
    // Si ya está en formato HH:MM, devolverlo directamente
    if (typeof iso === 'string' && /^\d{1,2}:\d{2}$/.test(iso)) {
      console.log('✅ Hora ya en formato HH:MM:', iso);
      return iso.length === 4 ? '0' + iso : iso; // Agregar 0 inicial si es necesario (ej: "7:00" -> "07:00")
    }
    
    try {
      // Si está en formato ISO, parsearlo
      const d = new Date(iso);
      // Verificar que la fecha sea válida
      if (isNaN(d.getTime())) {
        console.warn('⚠️ Fecha ISO inválida, devolviendo valor original:', iso);
        // Si no es una fecha ISO válida, pero parece una hora, devolverla
        if (typeof iso === 'string' && /^\d{1,2}:\d{2}/.test(iso)) {
          return iso.substring(0, 5); // Solo los primeros 5 caracteres HH:MM
        }
        return "";
      }
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      return `${hh}:${mm}`; // formato HH:MM para input[type=time]
    } catch (error) {
      console.error('❌ Error parseando fecha ISO:', iso, error);
      // Si hay error pero parece una hora, intentar devolverla
      if (typeof iso === 'string' && /^\d{1,2}:\d{2}/.test(iso)) {
        return iso.substring(0, 5);
      }
      return "";
    }
  };

  const getTimezoneOffsetString = () => {
    // Ej: "-04:00" o "+02:00"
    const offsetMinutes = -new Date().getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const abs = Math.abs(offsetMinutes);
    const hours = String(Math.floor(abs / 60)).padStart(2, "0");
    const minutes = String(abs % 60).padStart(2, "0");
    return `${sign}${hours}:${minutes}`;
  };

  const generateOrderNumber = () => {
    // OS-YYYYMMDD-<8 chars>
    const date = getLocalDateString();
    const suffix = Math.random().toString(36).substring(2, 10); // 8 chars
    return `OS-${date.replace(/-/g, "")}-${suffix}`;
  };

  // Inicializar form: si viene "orden" (editar), usar sus valores parseados,
  // si es nueva, fijar fecha local y técnico logeado
  const [form, setForm] = useState(() => {
    if (orden) {
      // Modo Edición: Cargar datos de la orden existente
      console.log('🔧 INICIALIZANDO FORM CON ORDEN EXISTENTE:', orden);
      console.log('🔧 ORDEN COMPLETA:', JSON.stringify(orden, null, 2));
      const nombreTecnicoExistente =
        deriveNombreTecnicoDesdeOrden(orden) ||
        buildDisplayNameFromMaybeObject(orden.nombre_tecnico) ||
        buildDisplayNameFromMaybeObject(orden.tecnico_nombre) ||
        "";
      return {
        cliente_id: orden.cliente_id || "",
        nombre_cliente: orden.nombre_cliente || "",
        sucursal: orden.sucursal || "",
        tipo_servicio: orden.tipo_servicio || "mantenimiento_preventivo",
        es_mantenimiento_preventivo: orden.es_mantenimiento_preventivo !== undefined ? orden.es_mantenimiento_preventivo : true,
        tecnico_id: orden.tecnico_id || (user?.id || user?._id) || "",
        nombre_tecnico: nombreTecnicoExistente,
        tecnico_nombre: nombreTecnicoExistente,
        fecha: parseISOToLocalDate(orden.fecha),
        hora_inicio: (() => {
          console.log('🕐 Parsing hora_inicio:', orden.hora_inicio, 'tipo:', typeof orden.hora_inicio);
          const parsed = parseISOToLocalTime(orden.hora_inicio);
          console.log('🕐 Resultado parsed hora_inicio:', parsed);
          return parsed;
        })(),
        hora_fin: (() => {
          console.log('🕑 Parsing hora_fin:', orden.hora_fin, 'tipo:', typeof orden.hora_fin);
          const parsed = parseISOToLocalTime(orden.hora_fin);
          console.log('🕑 Resultado parsed hora_fin:', parsed);
          return parsed;
        })(),
        equipos_ids: (() => {
          // Priorizar equipos_ids; si no hay, derivar de equipos (detalles)
          if (Array.isArray(orden.equipos_ids) && orden.equipos_ids.length > 0) return orden.equipos_ids;
          if (Array.isArray(orden.equipos) && orden.equipos.length > 0) {
            return orden.equipos.map(eq => eq._id || eq.id).filter(Boolean);
          }
          return [];
        })(),
        equipo_descripcion: orden.equipo_descripcion || "",
        trabajos_realizados: orden.trabajos_realizados || "",
        observaciones: orden.observaciones || "",
        firma_cliente: orden.firma_cliente || "",
        firma_aclaracion: (() => {
          const valor = orden.firma_aclaracion || orden.aclaracion_firma_cliente || "";
          console.log('✍️ Campos de firma disponibles:', {
            firma_aclaracion: orden.firma_aclaracion,
            aclaracion_firma_cliente: orden.aclaracion_firma_cliente,
            valor_final: valor
          });
          return valor;
        })(),
        estado: orden.estado || "borrador",
        es_servicio_programado: orden.es_servicio_programado || false,
        agendamiento_id: orden.agendamiento_id || "",
        origen: orden.origen || "interno",
        ticket_origen_id: orden.ticket_origen_id || "",
        departamentos: orden.departamentos || [],
      };
    } else {
      // Modo Creación: Inicializar formulario con valores por defecto
      const nombreTecnicoPorDefecto =
        (user?.rol || "").toLowerCase() === "tecnico"
          ? currentUserDisplayName
          : "";
      return {
        numero_orden: generateOrderNumber(),
        cliente_id: "",
        nombre_cliente: "",
        sucursal: "",
        tipo_servicio: "mantenimiento_preventivo",
        es_mantenimiento_preventivo: true,
        tecnico_id: (user?.rol || "").toLowerCase() === "tecnico" ? user?.id || user?._id : "",
        nombre_tecnico: nombreTecnicoPorDefecto,
        tecnico_nombre: nombreTecnicoPorDefecto,
        fecha: getLocalDateString(),
        hora_inicio: parseISOToLocalTime(new Date().toISOString()),
        hora_fin: "",
        equipos_ids: [],
        equipo_descripcion: "",
        trabajos_realizados: "",
        observaciones: "",
        firma_cliente: "",
        firma_aclaracion: "",
        estado: "borrador",
        es_servicio_programado: false,
        agendamiento_id: "",
        origen: "interno",
        ticket_origen_id: "",
        departamentos: [],
      };
    }
  });

  // Cargar detalle completo con firmas cuando se edita una orden existente (safety net)
  // Normalmente OrdenesList ya obtiene el detalle completo antes de abrir el form,
  // pero este efecto actúa como respaldo en caso de que la orden llegue incompleta.
  useEffect(() => {
    if (!orden) return;
    const ordenId = orden._id || orden.id;
    if (!ordenId) return;
    // Si ya tiene firma_cliente definida (no undefined), no necesita recargar
    if (orden.firma_cliente !== undefined) return;

    const cargarDetalleCompleto = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await http.get(`/ordenes/${ordenId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const detalle = res.data;
        // Actualizar solo los campos de firma en el form sin perder el resto
        setForm(prev => ({
          ...prev,
          firma_cliente: detalle.firma_cliente || prev.firma_cliente || "",
          firma_aclaracion: detalle.firma_aclaracion || detalle.aclaracion_firma_cliente || detalle.aclaracion_firma || prev.firma_aclaracion || "",
        }));
        // También actualizar el canvas de firma si existe
        if (sigCanvas.current && detalle.firma_cliente) {
          try {
            sigCanvas.current.fromDataURL(detalle.firma_cliente);
          } catch (e) {
            console.warn("Error cargando firma en canvas:", e);
          }
        }
      } catch (err) {
        console.error("Error cargando detalle completo de orden (firmas):", err);
      }
    };
    cargarDetalleCompleto();
  }, [orden]);

  useEffect(() => {
    const cargarDatosIniciales = async () => {
      await cargarClientes();
      if ((user?.rol || "").toLowerCase() === "admin") {
        await cargarTecnicos();
      } else if ((user?.rol || "").toLowerCase() === "tecnico") {
        setForm((prev) => {
          if (!prev.tecnico_id || prev.nombre_tecnico) return prev;
          if (
            prev.tecnico_id !== user?._id &&
            prev.tecnico_id !== user?.id
          ) {
            return prev;
          }
          return {
            ...prev,
            nombre_tecnico: currentUserDisplayName,
            tecnico_nombre: currentUserDisplayName,
          };
        });
      }
      // si se abre una orden y tiene cliente_id, cargar equipos y sucursales solo si no están ya cargados
      if (orden?.cliente_id) {
        console.log('🔧 Cargando datos para orden existente - cliente_id:', orden.cliente_id);
        
        // Solo cargar equipos si la lista está vacía o es de otro cliente
        if (todosLosEquipos.length === 0) {
          cargarEquipos(orden.cliente_id).catch(err => {
            console.warn('⚠️ Error cargando equipos en modo edición, continuando sin afectar form:', err);
          });
        } else {
          console.log('📋 Equipos ya disponibles, saltando recarga');
        }
        
        // Para las sucursales: si la orden tiene sucursal pero no se puede cargar la lista,
        // agregar la sucursal de la orden al array para que se muestre correctamente
        if (sucursales.length === 0) {
          cargarSucursales(orden.cliente_id).catch(err => {
            console.warn('⚠️ Error cargando sucursales en modo edición, usando sucursal de la orden:', err);
            // Si no se pueden cargar las sucursales y la orden tiene una, agregarla
            if (orden.sucursal) {
              console.log('🏢 Agregando sucursal de la orden:', orden.sucursal);
              setSucursales([orden.sucursal]);
            }
          });
          // Cargar departamentos del cliente
          const token = localStorage.getItem('token');
          http.get(`/clientes/${orden.cliente_id}/departamentos`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(r => setDepartamentosDisponibles(Array.isArray(r.data) ? r.data : [])).catch(() => setDepartamentosDisponibles([]));
        } else {
          console.log('🏢 Sucursales ya disponibles, saltando recarga');
        }

        // Cargar tickets si es origen ticket
        if (orden.origen === 'ticket' || form.origen === 'ticket') {
           // Resolver UUID del cliente para que el backend encuentre tickets correctamente
           const clienteParaTickets = clientes.find(c => c._id === orden.cliente_id || c.id === orden.cliente_id);
           const ticketClienteId = clienteParaTickets?.id || orden.cliente_id;
           cargarTickets(ticketClienteId).catch(console.error);
        }

        // Cargar agendamientos si es servicio programado
        if (orden.es_servicio_programado && orden.agendamiento_id) {
           const clienteParaAg = clientes.find(c => c._id === orden.cliente_id || c.id === orden.cliente_id);
           const agClienteId = clienteParaAg?.id || orden.cliente_id;
           cargarAgendamientos(agClienteId).catch(console.error);
        }
      }
      // Marcar que la carga inicial está completa después de un pequeño delay
      setTimeout(() => {
        setCargaInicialCompleta(true);
        inicializadoRef.current = true;
        setModoInicializacion(false);
        console.log('✅ Carga inicial completada - Modo inicialización deshabilitado');
        console.log('📊 Estado final del form después de inicialización:', form);
      }, 1200);
    };
    
    cargarDatosIniciales();
  }, []);

  // Efecto para pre-llenar el formulario desde un agendamiento
  useEffect(() => {
    if (!agendamientoInicial || orden) return; // Solo para órdenes nuevas
    
    const preCargarDesdeAgendamiento = async () => {
      const ag = agendamientoInicial;
      const clienteId = ag.cliente_id || ag.cliente?._id || ag.cliente?.id || '';
      const clienteNombre = ag.cliente?.nombre || ag.nombre_cliente || '';
      
      // Mapeo de tipo_mantenimiento a tipo_servicio
      const tipoMap = {
        'preventivo': 'mantenimiento_preventivo',
        'correctivo': 'mantenimiento_correctivo',
        'revision': 'revision',
        'limpieza': 'limpieza',
        'actualizacion': 'actualizacion',
        'presupuesto': 'presupuesto'
      };
      const tipoServicio = tipoMap[ag.tipo_mantenimiento] || 'mantenimiento_preventivo';
      
      // Extraer IDs de equipos
      const equiposIds = Array.isArray(ag.equipos) 
        ? ag.equipos.map(eq => eq._id || eq.id).filter(Boolean)
        : Array.isArray(ag.equipos_ids) ? ag.equipos_ids : [];
      
      console.log('📋 Pre-llenando formulario desde agendamiento:', ag.titulo);
      
      setForm(prev => ({
        ...prev,
        cliente_id: clienteId,
        nombre_cliente: clienteNombre,
        sucursal: ag.sucursal || '',
        tipo_servicio: tipoServicio,
        es_mantenimiento_preventivo: tipoServicio === 'mantenimiento_preventivo',
        fecha: ag.fecha_programada ? ag.fecha_programada.split('T')[0] : prev.fecha,
        hora_inicio: ag.hora_inicio ? ag.hora_inicio.substring(0, 5) : prev.hora_inicio,
        hora_fin: ag.hora_fin ? ag.hora_fin.substring(0, 5) : '',
        equipos_ids: equiposIds,
        descripcion_problema: ag.descripcion || '',
        observaciones: ag.observaciones || '',
        es_servicio_programado: true,
        agendamiento_id: ag._id || ag.id || '',
        tecnico_id: ag.tecnico_asignado_id || prev.tecnico_id || '',
        nombre_tecnico: ag.tecnico_asignado || prev.nombre_tecnico || '',
        tecnico_nombre: ag.tecnico_asignado || prev.tecnico_nombre || '',
      }));
      
      // Cargar datos del cliente (sucursales, equipos)
      if (clienteId) {
        try {
          await cargarSucursales(clienteId);
          await cargarEquipos(clienteId);
          // Cargar agendamientos del cliente para el dropdown
          await cargarAgendamientos(clienteId);
        } catch (err) {
          console.warn('⚠️ Error cargando datos del cliente desde agendamiento:', err);
        }
      }
    };
    
    // Esperar a que la carga inicial termine
    const timer = setTimeout(preCargarDesdeAgendamiento, 1500);
    return () => clearTimeout(timer);
  }, [agendamientoInicial, orden]);

  // Efecto para filtrar equipos cuando se carguen y ya hay una sucursal o departamentos seleccionados
  useEffect(() => {
    if (todosLosEquipos.length > 0) {
      filtrarEquiposPorSucursal(form.sucursal, form.departamentos);
    }
  }, [todosLosEquipos]);

  // Efecto para cargar firma en canvas principal
  useEffect(() => {
    if (sigCanvas.current) {
      if (form.firma_cliente) {
        try {
          sigCanvas.current.fromDataURL(form.firma_cliente);
        } catch (e) {
          sigCanvas.current.clear();
        }
      } else {
        sigCanvas.current.clear();
      }
    }
  }, [orden, form.firma_cliente]);

  // Efecto para cargar firma en canvas de pantalla completa y ajustar tamaño dinámicamente
  useEffect(() => {
    function updateDims() {
      // Usar casi todo el viewport, dejando margen para botones y padding
      const margin = 32;
      const w = Math.min(window.innerWidth - margin, 700);
      const h = Math.min(window.innerHeight - margin, 700);
      setFullScreenDims({ width: w, height: h });
    }
    if (showFullScreen) {
      updateDims();
      window.addEventListener("resize", updateDims);
      // Cargar firma existente
      setTimeout(() => {
        if (fullSigCanvas.current) {
          if (form.firma_cliente) {
            try {
              fullSigCanvas.current.fromDataURL(form.firma_cliente);
            } catch (e) {
              fullSigCanvas.current.clear();
            }
          } else {
            fullSigCanvas.current.clear();
          }
        }
      }, 100);
    }
    return () => {
      window.removeEventListener("resize", updateDims);
    };
  }, [showFullScreen]);

  async function cargarClientes() {
    console.log('👥 INICIO cargarClientes');
    try {
      const token = localStorage.getItem("token");
      console.log('👥 Token:', token ? 'Disponible' : 'NO DISPONIBLE');
      const res = await http.get("/clientes", {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('👥 Response status:', res.status);
      console.log('👥 Clientes cargados:', res.data?.length || 0);
      console.log('👥 Datos de clientes:', res.data);
      const clientesOrdenados = (res.data || []).sort((a, b) => 
        (a.nombre || "").toLowerCase().localeCompare((b.nombre || "").toLowerCase())
      );
      setClientes(clientesOrdenados);
      console.log('👥 Estado clientes actualizado');
    } catch (err) {
      console.error("❌ Error cargando clientes", err);
    }
  }

  async function cargarTecnicos() {
    if ((user?.rol || "").toLowerCase() !== "admin") {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const res = await http.get("/usuarios", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const tecnicosSolo = (res.data || []).filter(
        (u) => (u.rol || "").toLowerCase() === "tecnico"
      );
      const ordenados = tecnicosSolo.sort((a, b) => {
        const nombreA = buildDisplayNameFromUser(a) || a.username || "";
        const nombreB = buildDisplayNameFromUser(b) || b.username || "";
        return nombreA.localeCompare(nombreB);
      });
      setTecnicos(ordenados);
      setForm((prev) => {
        if (!prev.tecnico_id) return prev;
        const encontrado = ordenados.find(
          (t) => t.id === prev.tecnico_id || t._id === prev.tecnico_id
        );
        if (!encontrado) return prev;
        const display = buildDisplayNameFromUser(encontrado);
        if (display && display !== prev.nombre_tecnico) {
          return {
            ...prev,
            nombre_tecnico: display,
            tecnico_nombre: display,
          };
        }
        return prev;
      });
    } catch (err) {
      console.error("Error cargando técnicos", err);
    }
  }

  async function cargarSucursales(clienteId) {
    try {
      if (!clienteId) {
        console.log("📍 Cliente ID vacío, limpiando sucursales");
        setSucursales([]);
        return;
      }
      const token = localStorage.getItem("token");
      console.log("📍 INICIO cargarSucursales para cliente:", clienteId);
      console.log("📍 Token:", token ? "Disponible" : "No disponible");
      console.log("📍 URL:", `/clientes/${clienteId}/sucursales`);
      
      const res = await http.get(`/clientes/${clienteId}/sucursales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("📍 Response status:", res.status);
      console.log("📍 Sucursales recibidas:", res.data);
      console.log("📍 Tipo de datos:", typeof res.data, Array.isArray(res.data));
      
      // Filtrar "Principal" si viene del backend, ya que el usuario reporta que es una sucursal fantasma
      const sucursalesLimpias = (res.data || []).filter(s => s !== "Principal");
      setSucursales(sucursalesLimpias);
      console.log("📍 Estado sucursales actualizado (filtrado Principal)");
    } catch (err) {
      console.error("❌ Error completo cargando sucursales:", err);
      console.error("❌ Error response:", err.response);
      console.error("❌ Error status:", err.response?.status);
      console.error("❌ Error data:", err.response?.data);
      // Fallback: derivar sucursales en frontend desde cliente y equipos cargados
      try {
        const clienteSel = clientes.find(c => c._id === clienteId || c.id === clienteId);
        const base = new Set();
        if (clienteSel && Array.isArray(clienteSel.sucursales)) {
          // Normalizar: sucursales pueden ser strings o objetos {nombre: "..."}
          clienteSel.sucursales.forEach(s => {
            if (typeof s === 'string' && s.trim()) base.add(s.trim());
            else if (s && typeof s === 'object' && s.nombre) base.add(s.nombre);
          });
        }
        // Si ya tenemos todosLosEquipos cargados para este cliente, complementar
        todosLosEquipos.filter(eq => (eq.cliente_id === clienteId) || (eq.cliente?.id === clienteId) || (eq.cliente?._id === clienteId))
          .map(eq => eq.sucursal)
          .filter(Boolean)
          .forEach(s => base.add(s));
        const lista = Array.from(base).filter(s => s !== "Principal");
        // if (lista.length === 0) lista.push("Principal"); // Eliminado para evitar sucursal fantasma
        setSucursales(lista);
        console.warn("📍 Usando sucursales derivadas en frontend (filtrado Principal):", lista);
      } catch (fErr) {
        console.warn("📍 Fallback de sucursales no disponible, estableciendo [] o sucursal actual si existe", fErr);
        if (!orden) setSucursales([]);
      }
    }
  }

  async function cargarAgendamientos(clienteId) {
    try {
      if (!clienteId) {
        setAgendamientos([]);
        return;
      }
      
      setCargandoAgendamientos(true);
      const token = localStorage.getItem("token");
      
      // Obtener agendamientos del cliente (futuras y actuales)
      const res = await http.get(`/agendamientos-inteligentes/cliente/${clienteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtrar solo agendamientos programados, pendientes, confirmados o en progreso
      const agendamientosFiltrados = (res.data || []).filter(
        ag => ["programado", "pendiente", "confirmado", "en_progreso", "en_proceso"].includes(ag.estado)
      );
      
      setAgendamientos(agendamientosFiltrados);
      setCargandoAgendamientos(false);
    } catch (err) {
      console.error("Error cargando agendamientos:", err);
      setAgendamientos([]);
      setCargandoAgendamientos(false);
    }
  }

  async function cargarTickets(clienteId) {
    try {
      if (!clienteId) {
        setTicketsCliente([]);
        return;
      }
      
      setCargandoTickets(true);
      const token = localStorage.getItem("token");
      
      // Obtener tickets reales del nuevo endpoint
      // Si estamos editando y ya tiene ticket, necesitamos traer también los 'en_proceso'
      // para que el ticket seleccionado aparezca en el select
      const params = {
        cliente_id: clienteId
      };
      
      // Si estamos en modo edición o si el backend soporta múltiples estados, mejor no filtrar por estado estricto
      // o filtrar por 'pendiente' y 'en_proceso'
      // Por ahora quitamos el filtro de estado estricto en frontend para filtrar luego en memoria o traer todo
      
      const res = await http.get(`/tickets/`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filtramos en cliente para mostrar solo disponibles + el seleccionado actual
      const ticketsFiltrados = (res.data || []).filter(t => 
        t.estado === 'pendiente' || 
        t.estado === 'en_proceso' || 
        (form.ticket_origen_id && (t._id === form.ticket_origen_id || t.id === form.ticket_origen_id))
      );
      
      console.log('🎫 Tickets cargados:', ticketsFiltrados.length);
      setTicketsCliente(ticketsFiltrados);
      setCargandoTickets(false);
    } catch (err) {
      console.error("Error cargando tickets:", err);
      setTicketsCliente([]);
      setCargandoTickets(false);
    }
  }

  async function cargarEquipos(clienteId) {
    try {
      if (!clienteId) {
        console.log("🛠️ Cliente ID vacío, limpiando equipos");
        setEquipos([]);
        setTodosLosEquipos([]);
        return;
      }
      const token = localStorage.getItem("token");
      console.log("🛠️ INICIO cargarEquipos para cliente:", clienteId);
      console.log("🛠️ Token:", token ? "Disponible" : "No disponible");
      
      // Cargar TODOS los equipos (igual que PresupuestoForm)
      const res = await http.get("/equipos/", {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("🛠️ Response status:", res.status);
      console.log("🔍 Total equipos en sistema:", res.data?.length || 0);
      console.log("🔍 Cliente ID a buscar:", clienteId);
      
      // Filtrar equipos que pertenecen al cliente (misma lógica que PresupuestoForm)
      const todosEquipos = res.data || [];
      
      console.log('🔍 DEBUG: clienteId recibido:', clienteId);
      console.log('🔍 DEBUG: Total equipos disponibles:', todosEquipos.length);
      console.log('🔍 DEBUG: Clientes cargados:', clientes.length);
      console.log('🔍 DEBUG: Primeros 3 clientes:', clientes.slice(0, 3).map(c => ({id: c.id, _id: c._id, nombre: c.nombre})));
      
      if (todosEquipos.length > 0) {
        console.log('🔍 DEBUG: Primer equipo estructura:', todosEquipos[0]);
        console.log('🔍 DEBUG: Todos los cliente_id en equipos:', [...new Set(todosEquipos.map(eq => eq.cliente_id))]);
      }
      
      const equiposDelCliente = todosEquipos.filter(equipo => {
        // Buscar por cliente_id (UUID o ObjectId)
        const coincidePorId = equipo.cliente_id === clienteId || 
                             equipo.cliente?._id === clienteId ||
                             equipo.cliente?.id === clienteId;
        
        // Buscar por nombre del cliente (para equipos con IDs antiguos)
        const clienteSeleccionado = clientes.find(c => c.id === clienteId || c._id === clienteId);
        const coincidePorNombre = clienteSeleccionado && 
                                 (equipo.cliente?.nombre === clienteSeleccionado.nombre ||
                                  (equipo.cliente_nombre && equipo.cliente_nombre === clienteSeleccionado.nombre));
        
        const coincide = coincidePorId || coincidePorNombre;
        
        if (coincide) {
          console.log('✅ DEBUG: Equipo encontrado:', equipo.nombre || equipo.modelo, 
                     'cliente_id:', equipo.cliente_id, 
                     'cliente_nombre:', equipo.cliente?.nombre || equipo.cliente_nombre);
        }
        
        return coincide;
      });
      
      console.log("🛠️ Equipos del cliente cargados:", equiposDelCliente.length, 'de', todosEquipos.length, 'total');
      
      // Guardar todos los equipos del cliente (sin filtrar por sucursal)
      setTodosLosEquipos(equiposDelCliente);
      // Inicialmente mostrar todos los equipos (se filtrará cuando se seleccione sucursal)
      setEquipos(equiposDelCliente);
      console.log("🛠️ Estado equipos actualizado");
    } catch (err) {
      console.error("❌ Error completo cargando equipos:", err);
      console.error("❌ Error response:", err.response);
      console.error("❌ Error status:", err.response?.status);
      console.error("❌ Error data:", err.response?.data);
      
      // Solo limpiar equipos si NO estamos en modo edición
      if (!orden) {
        setTodosLosEquipos([]);
        setEquipos([]);
      }
      
      // Re-throw para que el catch en useEffect pueda manejarlo
      throw err;
    }
  }

  // Función para filtrar equipos por sucursal y departamentos seleccionados
  function filtrarEquiposPorSucursal(sucursal, departamentos) {
    const depts = departamentos || form.departamentos || [];
    let filtrados = todosLosEquipos;
    
    if (sucursal) {
      filtrados = filtrados.filter(eq => eq.sucursal === sucursal);
    }
    if (depts.length > 0) {
      filtrados = filtrados.filter(eq => !eq.departamento || depts.includes(eq.departamento));
    }
    
    // Siempre incluir equipos ya seleccionados para no perderlos
    const selectedIds = form.equipos_ids || [];
    if (selectedIds.length > 0) {
      const filtradosIds = new Set(filtrados.map(eq => eq._id || eq.id));
      const seleccionadosFaltantes = todosLosEquipos.filter(eq => {
        const eqId = eq._id || eq.id;
        return selectedIds.includes(eqId) && !filtradosIds.has(eqId);
      });
      filtrados = [...filtrados, ...seleccionadosFaltantes];
    }
    
    setEquipos(filtrados);
  }

  // Firma (canvas)
  function limpiarFirma() {
    if (sigCanvas.current) sigCanvas.current.clear();
    setForm({ ...form, firma_cliente: "" });
  }
  // Reescalar imagen base64 a tamaño estándar
  function scaleDataUrlToStandard(dataUrl) {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = function () {
        const canvas = document.createElement("canvas");
        canvas.width = STANDARD_SIG_WIDTH;
        canvas.height = STANDARD_SIG_HEIGHT;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // Ajustar la firma al centro y escalar proporcionalmente
        const scale = Math.min(
          STANDARD_SIG_WIDTH / img.width,
          STANDARD_SIG_HEIGHT / img.height
        );
        const w = img.width * scale;
        const h = img.height * scale;
        const x = (STANDARD_SIG_WIDTH - w) / 2;
        const y = (STANDARD_SIG_HEIGHT - h) / 2;
        ctx.drawImage(img, x, y, w, h);
        resolve(canvas.toDataURL("image/png"));
      };
      img.src = dataUrl;
    });
  }

  async function guardarFirma() {
    if (sigCanvas.current) {
      const data = sigCanvas.current.getCanvas().toDataURL("image/png");
      const scaled = await scaleDataUrlToStandard(data);
      setForm({ ...form, firma_cliente: scaled });
    }
  }

  // Guardar: convertir fecha y horas a ISO con offset local para evitar shifts
  async function guardarOrden() {
    // Validación: Si hay sucursales disponibles, es obligatorio seleccionar una
    if (sucursales.length > 0 && !form.sucursal) {
      alert("El cliente tiene sucursales registradas. Por favor seleccione una.");
      return;
    }

    // --- VALIDACIÓN DE FIRMA Y ACLARACIÓN ---
    // Detectar si es servicio remoto (case-insensitive)
    const esServicioRemoto = form.tipo_servicio 
      ? form.tipo_servicio.toLowerCase().includes('remoto') 
      : false;

    // Si intenta completar la orden y NO es servicio remoto
    if (form.estado === 'completada' && !esServicioRemoto) {
      // Verificar si hay firma (ya sea nueva en canvas o url existente)
      const tieneFirma = (sigCanvas.current && !sigCanvas.current.isEmpty()) || form.firma_cliente;
      
      if (!tieneFirma) {
        alert('⚠️ ALERTA: Falta la Firma del Cliente.\n\nSolo los servicios remotos pueden cerrarse sin firma. Por favor, solicite la firma del cliente.');
        return;
      }

      if (!form.firma_aclaracion || form.firma_aclaracion.trim() === '') {
        alert('⚠️ ALERTA: Falta la Aclaración de Firma.\n\nEs obligatorio ingresar el nombre de la persona que firmó la orden.');
        return;
      }
    }
    // ----------------------------------------------

    try {
      const token = localStorage.getItem("token");

      // Crear payload desde el form (clon)
      const payload = { ...form };
      
      // Debug: verificar equipos antes de enviar
      console.log('🔧 [DEBUG] Equipos antes de enviar:', {
        equipos_ids: form.equipos_ids,
        equipo_descripcion: form.equipo_descripcion,
        payload_equipos_ids: payload.equipos_ids,
        payload_equipo_descripcion: payload.equipo_descripcion
      });

      // Mapear firma_aclaracion a aclaracion_firma para consistencia con backend
      console.log('💾 Antes del mapeo firma_aclaracion:', payload.firma_aclaracion);
      if (payload.firma_aclaracion) {
        payload.aclaracion_firma = payload.firma_aclaracion;
        // Mantener firma_aclaracion por si acaso el backend antiguo lo usa, o borrarlo?
        // Mejor enviar ambos o solo el nuevo. Schemas espera 'aclaracion_firma'.
        // Borramos el viejo si existia mapeo anterior incorrecto
        if (payload.aclaracion_firma_cliente) delete payload.aclaracion_firma_cliente;
        console.log('💾 Mapeado a aclaracion_firma:', payload.aclaracion_firma);
      } else {
        console.log('💾 No hay firma_aclaracion para mapear');
      }

      // Tomar la firma actual del canvas si existe
      if (sigCanvas.current) {
        const data = sigCanvas.current.getCanvas().toDataURL("image/png");
        payload.firma_cliente = await scaleDataUrlToStandard(data);
      }

      // Si es nueva orden, generar número de orden en frontend
      if (!orden) {
        payload.numero_orden = generateOrderNumber();
      } else {
        // Si la orden ya tiene número, conservarlo (si existe)
        if (orden.numero_orden) payload.numero_orden = orden.numero_orden;
      }

      if (!payload.tecnico_id && (user?.rol || "").toLowerCase() === "tecnico") {
        payload.tecnico_id = user?._id || user?.id || payload.tecnico_id;
      }

      const tecnicoSeleccionado = tecnicos.find(
        (t) =>
          t.id === payload.tecnico_id ||
          t._id === payload.tecnico_id ||
          t.id === form.tecnico_id ||
          t._id === form.tecnico_id
      );

      const nombreTecnicoFinal = resolveDisplayName({
        primary: payload.nombre_tecnico,
        secondary: [
          payload.tecnico_nombre,
          tecnicoSeleccionado,
          user &&
            (payload.tecnico_id === user?._id || payload.tecnico_id === user?.id)
            ? user
            : null,
        ],
        lookup: () => {
          if (tecnicoSeleccionado) {
            return buildDisplayNameFromUser(tecnicoSeleccionado);
          }
          if (
            user &&
            (payload.tecnico_id === user?._id || payload.tecnico_id === user?.id)
          ) {
            return currentUserDisplayName;
          }
          return "";
        },
      });

      if (nombreTecnicoFinal) {
        payload.nombre_tecnico = nombreTecnicoFinal;
        payload.tecnico_nombre = nombreTecnicoFinal;
      }

      const tz = getTimezoneOffsetString();

      // fecha => form.fecha (YYYY-MM-DD) => lo guardamos como YYYY-MM-DDT00:00:00±HH:MM
      if (payload.fecha) {
        payload.fecha = `${payload.fecha}T00:00:00${tz}`;
      }

      // hora_inicio y hora_fin: si están en formato HH:MM, convertir a ISO local con la fecha seleccionada
      if (form.hora_inicio) {
        // asegurarse de tener fecha en payload
        const fechaBase = payload.fecha ? payload.fecha.slice(0, 10) : getLocalDateString();
        payload.hora_inicio = `${fechaBase}T${form.hora_inicio}:00${tz}`;
      } else {
        payload.hora_inicio = payload.hora_inicio || "";
      }

      if (form.hora_fin) {
        const fechaBase = payload.fecha ? payload.fecha.slice(0, 10) : getLocalDateString();
        payload.hora_fin = `${fechaBase}T${form.hora_fin}:00${tz}`;
      } else {
        payload.hora_fin = payload.hora_fin || "";
      }

      // Mantener el identificador seleccionado para el cliente (UUID o _id)
      // No forzar conversión a _id, el backend ya resuelve ambos formatos.
      if (payload.cliente_id) {
        payload.cliente_id = String(payload.cliente_id);
      }

      // POST o PUT
      console.log('📤 ENVIANDO AL SERVIDOR:', {
        metodo: orden ? 'PUT' : 'POST',
        url: orden ? `/ordenes/${orden._id}` : '/ordenes',
        payload: payload
      });
      
      let response;
      if (orden) {
        response = await http.put(`/ordenes/${orden._id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        response = await http.post("/ordenes", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      
      console.log('📥 RESPUESTA DEL SERVIDOR:', response.data);

      if (onSaved) onSaved();
      if (onClose) onClose();
    } catch (err) {
      console.error("Error guardando orden", err);
      alert("Error al guardar la orden. Revisa la consola.");
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 p-6 flex justify-between items-center shrink-0">
          <div className="text-white">
            <h3 className="text-2xl font-bold flex items-center gap-2">
              {orden ? <PenTool className="w-6 h-6" /> : <FileText className="w-6 h-6" />}
              {orden ? "Editar Orden de Servicio" : "Nueva Orden de Servicio"}
            </h3>
            <p className="text-emerald-100 text-sm mt-1">
              {orden ? `Editando orden #${orden.numero_orden || 'Sin número'}` : "Complete los datos para crear una nueva orden"}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-gray-50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* Left Column: Client & Service Info */}
            <div className="space-y-6">
              
              {/* Client Section */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                  <User className="w-5 h-5 text-emerald-600" />
                  Información del Cliente
                </h4>
                
                <div className="space-y-4">
                  {/* Cliente Select */}
                  {(user?.rol === "admin" || user?.rol === "tecnico") && (
                    <div>
                      <SearchableSelect
                        label="Cliente"
                        placeholder="Seleccionar cliente..."
                        className="w-full"
                        value={form.cliente_id || ""}
                        options={clientes.map(c => ({ value: c.id || c._id, label: c.nombre }))}
                        onChange={async (val) => {
                          console.log('🔄 ====== CLIENTE ONCHANGE EJECUTADO ======');
                          console.log('🔄 Cliente seleccionado:', val);
                          console.log('🔄 Carga inicial completa:', cargaInicialCompleta);
                          console.log('🔄 Modo edición (!!orden):', !!orden);
                          console.log('🔄 modoInicializacion:', modoInicializacion);
                          console.log('🔄 inicializadoRef.current:', inicializadoRef.current);
                          console.log('🔄 Cliente actual en form:', form.cliente_id);
                          console.log('🔄 Cliente de orden original:', orden?.cliente_id);
                          console.log('🔄 ¿Valores iguales? val === form.cliente_id:', val === form.cliente_id);
                          
                          // PROTECCIÓN 1: Si estamos en modo inicialización
                          if (modoInicializacion) {
                            console.log('⏭️ BLOQUEADO: Saltando onChange - modo inicialización activo');
                            return;
                          }
                          
                          // PROTECCIÓN 2: Si no está completamente cargado
                          if (!cargaInicialCompleta || !inicializadoRef.current) {
                            console.log('⏭️ BLOQUEADO: Saltando onChange - carga inicial no completa');
                            return;
                          }
                          
                          // PROTECCIÓN 3: Si estamos en modo edición y el valor no cambió
                          if (orden && val === form.cliente_id) {
                            console.log('⏭️ BLOQUEADO: Cliente no cambió, saltando onChange');
                            return;
                          }
                          
                          console.log('✅ PROCESANDO: onChange del cliente - todas las validaciones pasadas');
                          
                          console.log('🔄 Procesando onChange del cliente');
                          console.log('🔄 Token disponible:', !!localStorage.getItem("token"));
                          
                          // buscar por id (UUID) o por _id (MongoDB) - mismo orden que en las opciones
                          const cliente = clientes.find((c) => (c.id === val) || (c._id === val));
                          console.log('👤 Cliente encontrado:', cliente);
                          console.log('👥 Clientes disponibles:', clientes.map(c => ({_id: c._id, id: c.id, nombre: c.nombre})));
                          
                          const nuevoForm = {
                            ...form,
                            cliente_id: val,
                            nombre_cliente: cliente ? (cliente.nombre || "") : "",
                            // Solo limpiar sucursal si NO estamos en modo edición o si realmente cambió el cliente
                            sucursal: orden && orden.cliente_id === val ? form.sucursal : "", 
                            // Reset origen a interno si se deselecciona el cliente
                            origen: val ? form.origen : "interno"
                          };
                          
                          console.log('📝 ACTUALIZANDO FORM (Cliente):', {
                            anterior_cliente_id: form.cliente_id,
                            nuevo_cliente_id: val,
                            anterior_sucursal: form.sucursal,
                            nueva_sucursal: nuevoForm.sucursal,
                            conserva_sucursal: orden && orden.cliente_id === val
                          });
                          
                          setForm(nuevoForm);
                          
                          // Solo recargar datos si no estamos en modo edición o si el cliente realmente cambió
                          const clienteCambio = !orden || (orden.cliente_id !== val);
                          if (val && clienteCambio) {
                            console.log('🔄 Cargando sucursales, equipos y agendamientos para cliente nuevo:', val);
                            // Cargar departamentos del cliente
                            setDepartamentosDisponibles([]);
                            try {
                              const tokenDept = localStorage.getItem('token');
                              const deptRes = await http.get(`/clientes/${val}/departamentos`, {
                                headers: { Authorization: `Bearer ${tokenDept}` }
                              });
                              setDepartamentosDisponibles(Array.isArray(deptRes.data) ? deptRes.data : []);
                            } catch (ed) {
                              setDepartamentosDisponibles([]);
                            }
                            // Ejecutar en serie pero sin bloquear cargarEquipos si cargarSucursales falla
                            try {
                              await cargarSucursales(val);
                            } catch (e1) {
                              console.warn('⚠️ cargarSucursales falló, continuando con cargarEquipos:', e1?.response?.status, e1?.message);
                            }
                            try {
                              await cargarEquipos(val);
                              console.log('✅ Equipos cargados (independiente de sucursales)');
                            } catch (e2) {
                              console.error('❌ cargarEquipos falló:', e2);
                            }
                            try {
                              // Usar el UUID del cliente para agendamientos (no el ObjectId)
                              const clienteIdParaAgendamientos = cliente?.id || val;
                              console.log('📅 Cargando agendamientos con ID:', clienteIdParaAgendamientos);
                              await cargarAgendamientos(clienteIdParaAgendamientos);
                              console.log('✅ Agendamientos cargados');
                            } catch (e3) {
                              console.error('❌ cargarAgendamientos falló:', e3);
                            }
                            try {
                              // Cargar tickets del cliente
                              const clienteIdParaTickets = cliente?.id || val; // Intentar con UUID primero, el backend maneja ambos
                              console.log('🎫 Cargando tickets con ID:', clienteIdParaTickets);
                              await cargarTickets(clienteIdParaTickets);
                              console.log('✅ Tickets cargados');
                            } catch (e4) {
                              console.error('❌ cargarTickets falló:', e4);
                            }
                          } else if (!val) {
                            console.log('⚠️ Cliente vacío, limpiando datos');
                            setSucursales([]);
                            setDepartamentosDisponibles([]);
                            setTodosLosEquipos([]);
                            setEquipos([]);
                            setAgendamientos([]);
                            setTicketsCliente([]);
                          } else {
                            console.log('⏭️ Modo edición - no recargando datos del mismo cliente');
                          }
                        }}
                      />
                    </div>
                  )}

                  {/* Sucursal Select */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sucursal
                      {sucursales.length > 0 && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <select
                      disabled={sucursales.length === 0}
                      className={`w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${sucursales.length === 0 ? 'bg-gray-100 text-gray-500' : ''}`}
                      value={form.sucursal || ""}
                      onChange={(e) => {
                        const sucursalSeleccionada = e.target.value;
                        console.log('🏢 ====== SUCURSAL ONCHANGE EJECUTADO ======');
                        console.log('🏢 Sucursal seleccionada:', sucursalSeleccionada);
                        console.log('🏢 Sucursal actual en form:', form.sucursal);
                        console.log('🏢 Modo inicialización:', modoInicializacion);
                        console.log('🏢 Carga inicial completa:', cargaInicialCompleta);
                        
                        // Si estamos en modo inicialización, solo actualizar el estado sin recargar
                        if (modoInicializacion) {
                          console.log('⏭️ BLOQUEADO: Saltando filtrado durante inicialización');
                          setForm({ ...form, sucursal: sucursalSeleccionada });
                          return;
                        }
                        
                        // Si no cambió el valor, no hacer nada
                        if (sucursalSeleccionada === form.sucursal) {
                          console.log('⏭️ BLOQUEADO: Sucursal no cambió');
                          return;
                        }
                        
                        console.log('✅ PROCESANDO: onChange de sucursal');
                        setForm({ ...form, sucursal: sucursalSeleccionada, departamentos: [] });
                        
                        // Filtrar equipos por la sucursal seleccionada
                        filtrarEquiposPorSucursal(sucursalSeleccionada, []);
                      }}
                    >
                      <option value="">-- Seleccionar sucursal --</option>
                      {(() => {
                        console.log('🔍 RENDERIZANDO SELECT SUCURSAL:');
                        console.log('🔍 Cantidad de sucursales disponibles:', sucursales.length);
                        console.log('🔍 form.sucursal:', form.sucursal);
                        console.log('🔍 Sucursales:', sucursales);
                        return sucursales.map((sucursal) => (
                          <option key={sucursal} value={sucursal}>
                            {sucursal}
                          </option>
                        ));
                      })()}
                    </select>
                    {sucursales.length > 0 && (
                      <small className="text-gray-500 text-xs mt-1 block">
                        {sucursales.length} sucursales disponibles
                      </small>
                    )}
                  </div>

                  {/* Departamentos / Áreas (multi-select) */}
                  {departamentosDisponibles.length > 0 && (() => {
                    const deptsFiltrados = form.sucursal
                      ? departamentosDisponibles.filter(d => !d.sucursal_nombre || d.sucursal_nombre === form.sucursal)
                      : departamentosDisponibles.filter(d => !d.sucursal_nombre);
                    if (deptsFiltrados.length === 0) return null;
                    return (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                          <Layers className="w-4 h-4" />
                          Departamentos / Áreas
                        </label>
                        <div className="border border-gray-300 rounded-lg p-2 max-h-40 overflow-y-auto bg-white space-y-1">
                          {deptsFiltrados.map((d, i) => (
                            <label key={i} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer text-sm">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                                checked={(form.departamentos || []).includes(d.nombre)}
                                onChange={(e) => {
                                  const current = form.departamentos || [];
                                  let next;
                                  if (e.target.checked) {
                                    next = [...current, d.nombre];
                                  } else {
                                    next = current.filter(x => x !== d.nombre);
                                  }
                                  setForm({ ...form, departamentos: next });
                                  filtrarEquiposPorSucursal(form.sucursal, next);
                                }}
                              />
                              {d.nombre}
                            </label>
                          ))}
                        </div>
                        {(form.departamentos || []).length > 0 && (
                          <small className="text-emerald-600 text-xs mt-1 block">
                            {form.departamentos.length} seleccionado(s)
                          </small>
                        )}
                      </div>
                    );
                  })()}

                  {/* TÉCNICO (solo admin puede cambiar) */}
                  {user?.rol === "admin" ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Técnico responsable</label>
                      <select
                        className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                        value={form.tecnico_id || ""}
                        onChange={(e) => {
                          const val = e.target.value;
                          const t = tecnicos.find((x) => x.id === val || x._id === val);
                          setForm({
                            ...form,
                            tecnico_id: val,
                            nombre_tecnico: t ? buildDisplayNameFromUser(t) : "",
                            tecnico_nombre: t ? buildDisplayNameFromUser(t) : "",
                          });
                        }}
                      >
                        <option value="">-- Seleccionar técnico --</option>
                        {tecnicos.map((t) => (
                          <option key={t.id || t._id} value={t.id || t._id}>
                            {buildDisplayNameFromUser(t) || t.username || t.email || t.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <span className="text-sm font-medium text-gray-500 block">Técnico Asignado</span>
                      <span className="text-gray-900 font-medium">
                        {resolveDisplayName({
                          primary: form.nombre_tecnico,
                          secondary: [form.tecnico_nombre, user],
                          lookup: () => currentUserDisplayName,
                        }) || "Sin asignar"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Service Details Section */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                  <Wrench className="w-5 h-5 text-emerald-600" />
                  Detalles del Servicio
                </h4>
                
                <div className="space-y-4">
                  {/* Origen Select */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Origen de la Orden</label>
                    <select
                      className={`w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors ${!form.cliente_id ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                      value={form.origen || "interno"}
                      disabled={!form.cliente_id}
                      onChange={(e) => {
                        const nuevoOrigen = e.target.value;
                        setForm({ 
                          ...form, 
                          origen: nuevoOrigen,
                          // Si cambia a algo que no es ticket, limpiar el ticket seleccionado
                          ticket_origen_id: nuevoOrigen !== 'ticket' ? "" : form.ticket_origen_id
                        });
                        
                        // Si selecciona ticket y no hay tickets cargados (y hay cliente), intentar cargar
                        if (nuevoOrigen === 'ticket' && form.cliente_id && ticketsCliente.length === 0) {
                           cargarTickets(form.cliente_id);
                        }
                      }}
                    >
                      <option value="interno">Interno (Creada por la empresa)</option>
                      <option value="ticket">Ticket de Soporte (Solicitada por cliente)</option>
                    </select>
                    {!form.cliente_id && (
                      <p className="text-xs text-gray-500 mt-1">
                        Seleccione un cliente para habilitar la opción de Ticket.
                      </p>
                    )}
                  </div>

                  {/* Ticket Selector (Visible solo si origen es ticket) */}
                  {form.origen === 'ticket' && (
                    <div className="animate-in slide-in-from-top-2 duration-200">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Seleccionar Ticket</label>
                      <select
                        className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                        value={form.ticket_origen_id || ""}
                        onChange={(e) => {
                          const ticketId = e.target.value;
                          const ticket = ticketsCliente.find(t => t._id === ticketId || t.id === ticketId);
                          
                          if (ticket) {
                            console.log('🎫 Ticket seleccionado:', ticket);
                            setForm({
                              ...form,
                              ticket_origen_id: ticketId,
                              // Auto-rellenado de datos del ticket
                              descripcion_problema: ticket.descripcion_problema || form.descripcion_problema,
                              sucursal: ticket.sucursal || form.sucursal,
                              // Si el ticket tiene equipo, seleccionarlo
                              equipos_ids: ticket.equipo_id ? [ticket.equipo_id] : form.equipos_ids,
                              equipo_descripcion: ticket.equipo_descripcion || form.equipo_descripcion,
                              // Si hay usuario afectado, agregarlo a observaciones si no está ya
                              observaciones: ticket.usuario_afectado 
                                ? (form.observaciones ? `${form.observaciones}\nUsuario afectado: ${ticket.usuario_afectado}` : `Usuario afectado: ${ticket.usuario_afectado}`)
                                : form.observaciones
                            });
                            
                            // Si el ticket tiene sucursal, filtrar equipos visualmente
                            if (ticket.sucursal) {
                                filtrarEquiposPorSucursal(ticket.sucursal, form.departamentos);
                            }
                          } else {
                            setForm({
                              ...form,
                              ticket_origen_id: ticketId
                            });
                          }
                        }}
                        disabled={cargandoTickets}
                      >
                        <option value="">-- Seleccionar ticket --</option>
                        {cargandoTickets ? (
                          <option value="" disabled>Cargando tickets...</option>
                        ) : ticketsCliente.length === 0 ? (
                          <option value="" disabled>No hay tickets pendientes</option>
                        ) : (
                          ticketsCliente.map((t) => (
                            <option key={t._id || t.id} value={t._id || t.id}>
                              Ticket - {t.numero_ticket} - {t.sucursal || 'Sin sucursal'} - {new Date(t.fecha_creacion).toLocaleDateString()}
                            </option>
                          ))
                        )}
                      </select>
                      {ticketsCliente.length === 0 && !cargandoTickets && (
                        <small className="text-amber-600 text-xs mt-1 block">
                          No se encontraron tickets pendientes para este cliente.
                        </small>
                      )}
                    </div>
                  )}

                  {/* Tipo Servicio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Servicio</label>
                    <select
                      className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      value={form.tipo_servicio || "mantenimiento_preventivo"}
                      onChange={(e) => {
                        const tipoSeleccionado = e.target.value;
                        setForm({
                          ...form,
                          tipo_servicio: tipoSeleccionado,
                          es_mantenimiento_preventivo: tipoSeleccionado === "mantenimiento_preventivo"
                        });
                      }}
                    >
                      <option value="mantenimiento_preventivo">Mantenimiento Preventivo</option>
                      <option value="correctivo">Mantenimiento Correctivo</option>
                      <option value="remoto">Servicio Remoto</option>
                      <option value="instalacion">Instalación</option>
                      <option value="reparacion">Reparación</option>
                      <option value="revision">Revisión</option>
                      <option value="diagnostico">Diagnóstico</option>
                      <option value="actualizacion">Actualización</option>
                      <option value="calibracion">Calibración</option>
                    </select>
                  </div>

                  {/* Programado Checkbox */}
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-100">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.es_servicio_programado || false}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setForm({
                            ...form,
                            es_servicio_programado: isChecked,
                            agendamiento_id: isChecked ? form.agendamiento_id : ""
                          });
                          
                          // Cargar agendamientos si se activa y hay cliente seleccionado
                          if (isChecked && form.cliente_id && agendamientos.length === 0) {
                            // Buscar el cliente para obtener su UUID
                            const clienteActual = clientes.find((c) => (c._id === form.cliente_id) || (c.id === form.cliente_id));
                            const clienteIdParaAgendamientos = clienteActual?.id || form.cliente_id;
                            console.log('📅 Checkbox: Cargando agendamientos con ID:', clienteIdParaAgendamientos);
                            cargarAgendamientos(clienteIdParaAgendamientos);
                          }
                        }}
                        className="mt-1 w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500 border-gray-300"
                      />
                      <div>
                        <span className="font-medium text-gray-900">¿Es un servicio agendado?</span>
                        <p className="text-xs text-gray-500 mt-1">
                          Marque esta opción para vincular un agendamiento pendiente.
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Agendamiento Select */}
                  {form.es_servicio_programado && (
                    <div className="animate-in slide-in-from-top-2 duration-200">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Agendamiento Asociado</label>
                      <select
                        className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                        value={form.agendamiento_id || ""}
                        onChange={(e) => {
                          const agendamientoId = e.target.value;
                          const agendamiento = agendamientos.find(ag => ag._id === agendamientoId);
                          
                          setForm(prev => {
                            const newState = {
                              ...prev,
                              agendamiento_id: agendamientoId
                            };
                            
                            // Si el agendamiento tiene sucursal, actualizarla
                            if (agendamiento?.sucursal) {
                              newState.sucursal = agendamiento.sucursal;
                            }
                            
                            // Si el agendamiento tiene equipos, pre-seleccionarlos
                            if (agendamiento?.equipos && Array.isArray(agendamiento.equipos)) {
                              newState.equipos_ids = agendamiento.equipos;
                            }
                            
                            return newState;
                          });
                        }}
                        disabled={!form.cliente_id || cargandoAgendamientos}
                      >
                        <option value="">-- Seleccionar agendamiento --</option>
                        {cargandoAgendamientos ? (
                          <option value="" disabled>Cargando agendamientos...</option>
                        ) : agendamientos.length === 0 ? (
                          <option value="" disabled>No hay agendamientos disponibles</option>
                        ) : (
                          agendamientos.map((ag) => (
                            <option key={ag._id} value={ag._id}>
                              {ag.titulo || 'Mantenimiento'} - {ag.sucursal || 'Sin sucursal'} - {new Date(ag.fecha_programada).toLocaleDateString()} 
                              {ag.equipos?.length ? ` (${ag.equipos.length} equipos)` : ''}
                            </option>
                          ))
                        )}
                      </select>
                      {!form.cliente_id && (
                        <small className="text-amber-600 text-xs mt-1 block">
                          Seleccione primero un cliente para ver sus agendamientos
                        </small>
                      )}
                      {form.cliente_id && agendamientos.length === 0 && !cargandoAgendamientos && (
                        <small className="text-gray-500 text-xs mt-1 block">
                          Este cliente no tiene agendamientos programados disponibles
                        </small>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Date & Time Section */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                  <Calendar className="w-5 h-5 text-emerald-600" />
                  Fecha y Hora
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fecha</label>
                    <input
                      type="date"
                      className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      value={form.fecha}
                      onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Inicio</label>
                    <input
                      type="time"
                      className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      value={form.hora_inicio || ""}
                      onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Fin</label>
                    <input
                      type="time"
                      className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      value={form.hora_fin || ""}
                      onChange={(e) => setForm({ ...form, hora_fin: e.target.value })}
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column: Equipment & Work */}
            <div className="space-y-6">
              
              {/* Equipment Section */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 h-fit">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                  <CheckSquare className="w-5 h-5 text-emerald-600" />
                  Equipos
                  <span className="ml-auto text-xs font-normal bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">
                    {(form.equipos_ids || []).length} seleccionados
                  </span>
                </h4>
                
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="max-h-60 overflow-y-auto p-2 bg-gray-50 space-y-1 custom-scrollbar">
                    {equipos.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        No hay equipos para el cliente o sucursal seleccionada.
                      </div>
                    ) : (
                      equipos.map((eq) => {
                        const equipoId = eq._id || eq.id;
                        const checked = (form.equipos_ids || []).includes(equipoId);
                        const label = eq.descripcion || eq.modelo || eq.nombre || equipoId;
                        return (
                          <label key={equipoId} className="flex items-center gap-3 p-2 hover:bg-white rounded-md transition-colores cursor-pointer group">
                            <input
                              type="checkbox"
                              className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                              checked={checked}
                              onChange={(e) => {
                                const next = new Set(form.equipos_ids || []);
                                if (e.target.checked) next.add(equipoId); else next.delete(equipoId);
                                const selectedIds = Array.from(next);
                                // Auto-detectar departamento del equipo seleccionado
                                let newDepts = form.departamentos || [];
                                if (e.target.checked && eq.departamento && !newDepts.includes(eq.departamento)) {
                                  newDepts = [...newDepts, eq.departamento];
                                }
                                setForm({ ...form, equipos_ids: selectedIds, departamentos: newDepts });
                              }}
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{label}</div>
                              {(eq.sucursal || eq.departamento) && (
                                <div className="text-xs text-gray-500">
                                  {[eq.sucursal, eq.departamento].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <div className="bg-gray-100 px-3 py-2 text-xs text-gray-500 border-t border-gray-200">
                    Mostrando {equipos.length} de {todosLosEquipos.length} equipos
                    {form.sucursal && ` (filtrados por sucursal: ${form.sucursal})`}
                  </div>
                </div>
              </div>

              {/* Work Description Section */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Descripción del Trabajo
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trabajos Realizados</label>
                    <textarea
                      className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors min-h-[100px]"
                      rows={4}
                      value={form.trabajos_realizados || ""}
                      onChange={(e) => setForm({ ...form, trabajos_realizados: e.target.value })}
                      placeholder="Describa detalladamente las tareas realizadas..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                    <textarea
                      className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      rows={2}
                      value={form.observaciones || ""}
                      onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                      placeholder="Notas adicionales..."
                    />
                  </div>
                </div>
              </div>

              {/* Signature Section */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2 border-b pb-2">
                  <PenTool className="w-5 h-5 text-emerald-600" />
                  Conformidad del Cliente
                </h4>
                
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 bg-gray-50 flex flex-col items-center justify-center">
                    <div style={{ width: 600, maxWidth: '100%' }} className="bg-white shadow-sm rounded-lg overflow-hidden">
                      <SignatureCanvas
                        ref={sigCanvas}
                        penColor="black"
                        canvasProps={{
                          width: 600,
                          height: 180,
                          className: "w-full h-[180px] cursor-crosshair"
                        }}
                      />
                    </div>
                    <div className="flex gap-2 mt-4 w-full justify-center">
                      <button 
                        type="button" 
                        onClick={limpiarFirma}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                      >
                        <Trash2 className="w-4 h-4" /> Limpiar
                      </button>
                      <button 
                        type="button" 
                        onClick={guardarFirma}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-md transition-colors"
                      >
                        <Save className="w-4 h-4" /> Guardar
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setShowFullScreen(true)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                      >
                        <Maximize2 className="w-4 h-4" /> Pantalla Completa
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Aclaración de Firma</label>
                    <input
                      type="text"
                      className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                      value={form.firma_aclaracion || ""}
                      onChange={(e) => setForm({ ...form, firma_aclaracion: e.target.value })}
                      placeholder="Nombre y apellido del firmante"
                    />
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 p-6 border-t border-gray-200 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              Estado:
              <select
                className="border-gray-300 rounded-md shadow-sm focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                value={form.estado}
                onChange={(e) => setForm({ ...form, estado: e.target.value })}
              >
                <option value="borrador">Borrador</option>
                <option value="completada">Completada</option>
              </select>
            </label>
          </div>
          
          <div className="flex gap-3">
            <button 
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancelar
            </button>
            <button 
              onClick={guardarOrden}
              className="px-6 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 shadow-md hover:shadow-lg transition-all flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Guardar Orden
            </button>
          </div>
        </div>

        {/* Modal de pantalla completa para firma */}
        {showFullScreen && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm">
            <div
              className="bg-white p-4 rounded-xl shadow-2xl flex flex-col items-center animate-in zoom-in duration-200"
              style={{ width: fullScreenDims.width, maxWidth: "98vw" }}
            >
              <p className="mb-4 font-semibold text-lg text-gray-800">Firma del cliente (pantalla completa)</p>
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden shadow-inner">
                <SignatureCanvas
                  ref={fullSigCanvas}
                  penColor="black"
                  canvasProps={{
                    width: fullScreenDims.width,
                    height: fullScreenDims.height - 80,
                    style: {
                      width: fullScreenDims.width,
                      height: fullScreenDims.height - 80,
                      touchAction: "none",
                      background: "#fff"
                    }
                  }}
                />
              </div>
              <div className="flex gap-3 mt-4 w-full justify-center">
                <button 
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors" 
                  type="button" 
                  onClick={() => { fullSigCanvas.current.clear(); }}
                >
                  Limpiar
                </button>
                <button 
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium shadow-md transition-colors" 
                  type="button" 
                  onClick={async () => {
                    // Guardar firma del canvas fullscreen en el form y cerrar modal, reescalando a tamaño estándar
                    const data = fullSigCanvas.current.getCanvas().toDataURL("image/png");
                    const scaled = await scaleDataUrlToStandard(data);
                    setForm({ ...form, firma_cliente: scaled });
                    setShowFullScreen(false);
                  }}
                >
                  Guardar firma
                </button>
                <button 
                  className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-medium transition-colors" 
                  type="button" 
                  onClick={() => setShowFullScreen(false)}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
